/**
 * AN ATTENTION BADGE ON A SIDEBAR ENTRY - the other half of the `NavRail` row
 * (`docs/ui/03-components.md`: "with attention badges", wrapping `NavLink` + `Indicator`).
 *
 * ★ THE NUMBER IS THE PLATFORM'S, OR THERE IS NONE. A badge shows only a count an operation served
 * - never a client-side count of a first page, never a guess. So an entry's attention is one of
 * three states, and each draws exactly one thing:
 *   loading  the count has been asked for and not yet answered: a skeleton the badge's size,
 *            hidden from assistive technology, because there is nothing to say yet;
 *   absent   no count was served - the read failed, the answer carried none, or there is nothing to
 *            read through: NOTHING is drawn. Never a zero and never a dash, because either would be
 *            a number the platform did not say;
 *   known    the count. Zero draws nothing (an entry with nothing waiting needs no badge); more
 *            than zero draws the number.
 *
 * ★ THE NUMBER IS IN THE ENTRY'S NAME, NOT ONLY IN THE BADGE. A digit beside a link is read, if at
 * all, as part of its text with no meaning - "Applications 3". So the badge is hidden from
 * assistive technology and `navEntryName` builds the sentence the entry is announced by -
 * "Applications, 3 open" - for the caller's `aria-label`. It starts with the visible label, so
 * speech input still matches what is on screen (WCAG 2.5.3, label in name).
 *
 * ★ A SIDEBAR THAT REMOUNTS DOES NOT FLASH. The cabinet mounts its shell per screen and the back
 * office per chrome, so `useNavAttention` remembers the last answer per scope - the client or
 * transport it reads through - and per identity, and shows it at once while it asks again. What is
 * remembered is a count the platform served moments ago, replaced by the next answer or dropped when
 * the next read fails.
 *
 * ★ A READ STARTS ONE TASK AFTER THE SIDEBAR SETTLES. A sidebar on its way out still re-renders
 * once with the next page's address before the router unmounts it, and StrictMode mounts every
 * sidebar twice in development; asking at once sent a read for each, only to abandon it. So the
 * reads wait for the next task and are cancelled with the effect - a sidebar that does not stay
 * sends nothing (`test/components/NavRail/nav-attention.spec.tsx`).
 */
import { useEffect, useEffectEvent, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Skeleton } from '@mantine/core';

export type NavAttention =
  | { readonly state: 'loading' }
  | { readonly state: 'absent' }
  | { readonly state: 'known'; readonly count: number };

const LOADING: NavAttention = { state: 'loading' };
const ABSENT: NavAttention = { state: 'absent' };

/** More than this and the badge says "99+"; the entry's name still says the exact number. */
const MOST_DRAWN = 99;

/**
 * What an entry draws in its right section for `attention`, or `undefined` for nothing - Mantine's
 * `NavLink` draws no section at all for `undefined`, where `null` would still draw an empty one.
 */
export function navAttentionSection(attention: NavAttention | undefined): ReactNode {
  if (attention === undefined || attention.state === 'absent') {
    return undefined;
  }
  if (attention.state === 'loading') {
    return (
      <Skeleton
        height={18}
        width={24}
        radius="xl"
        aria-hidden="true"
        data-nav-attention="loading"
      />
    );
  }
  if (attention.count <= 0) {
    return undefined;
  }
  return (
    <Badge
      component="span"
      size="sm"
      variant="filled"
      aria-hidden="true"
      data-nav-attention="known"
    >
      {attention.count > MOST_DRAWN ? `${String(MOST_DRAWN)}+` : String(attention.count)}
    </Badge>
  );
}

/**
 * The entry's accessible name when there is a count to say - `"Applications, 3 open"` - else
 * `undefined`, and the visible label names the entry as it always did. `says` words the count in
 * the entry's own terms: `(count) => \`${count} open\``.
 */
export function navEntryName(
  label: string,
  attention: NavAttention | undefined,
  says: (count: number) => string,
): string | undefined {
  if (attention?.state !== 'known' || attention.count <= 0) {
    return undefined;
  }
  return `${label}, ${says(attention.count)}`;
}

/** Where one entry's count comes from. */
export interface NavAttentionSource {
  /**
   * The platform's count, or `undefined` when the answer carried none. Rejects when the read fails.
   * `signal` aborts it when the sidebar moves on or unmounts.
   */
  readonly read: (signal: AbortSignal) => Promise<number | undefined>;
}

export interface NavAttentionOptions {
  /**
   * What the counts are read through - the client or transport instance - or `undefined` when there
   * is nothing to read through, and then every entry is `absent`. Answers are remembered per scope.
   */
  readonly scope: object | undefined;
  /** Whose counts they are, when one scope serves more than one person in a tab. */
  readonly identity?: string;
  /** Ask again whenever this changes: where the reader is. */
  readonly refreshKey: string;
}

/** The last answers, per scope, per identity and entry. A WeakMap, so a dropped client takes its own. */
const remembered = new WeakMap<object, Map<string, number>>();

function memoryOf(scope: object): Map<string, number> {
  let memory = remembered.get(scope);
  if (memory === undefined) {
    memory = new Map();
    remembered.set(scope, memory);
  }
  return memory;
}

function keyOf(identity: string, path: string): string {
  return `${identity}\u0000${path}`;
}

/** A count the contract allows: `total` is `integer, minimum: 0` on every page that carries one. */
function isCount(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value >= 0;
}

/**
 * Each entry's attention, keyed by the entry's path. `sources` names the entries that carry a
 * count - an entry absent from it gets no attention at all - and may be a new map on every render:
 * what is read is decided by its keys.
 */
export function useNavAttention(
  sources: ReadonlyMap<string, NavAttentionSource>,
  { scope, identity = '', refreshKey }: NavAttentionOptions,
): ReadonlyMap<string, NavAttention> {
  const paths = [...sources.keys()];
  const pathsKey = paths.join('\n');

  const [answers, setAnswers] = useState<ReadonlyMap<string, NavAttention>>(() => {
    const seeded = new Map<string, NavAttention>();
    if (scope !== undefined) {
      const memory = memoryOf(scope);
      for (const path of paths) {
        const count = memory.get(keyOf(identity, path));
        if (count !== undefined) {
          seeded.set(keyOf(identity, path), { state: 'known', count });
        }
      }
    }
    return seeded;
  });

  // The newest `sources` without making them a dependency: a caller may build the map per render.
  const readOne = useEffectEvent(
    (path: string, signal: AbortSignal): Promise<number | undefined> =>
      sources.get(path)?.read(signal) ?? Promise.resolve(undefined),
  );

  useEffect(() => {
    if (scope === undefined || pathsKey === '') {
      return undefined;
    }
    const controller = new AbortController();
    const memory = memoryOf(scope);
    const settle = (key: string, attention: NavAttention): void => {
      if (attention.state === 'known') {
        memory.set(key, attention.count);
      } else {
        memory.delete(key);
      }
      setAnswers((previous) => new Map(previous).set(key, attention));
    };
    const start = setTimeout(() => {
      for (const path of pathsKey.split('\n')) {
        const key = keyOf(identity, path);
        readOne(path, controller.signal).then(
          (count) => {
            if (!controller.signal.aborted) {
              settle(key, isCount(count) ? { state: 'known', count } : ABSENT);
            }
          },
          () => {
            if (!controller.signal.aborted) {
              settle(key, ABSENT);
            }
          },
        );
      }
    }, 0);
    return () => {
      clearTimeout(start);
      controller.abort();
    };
  }, [scope, identity, pathsKey, refreshKey]);

  return new Map(
    paths.map((path): [string, NavAttention] => [
      path,
      scope === undefined ? ABSENT : (answers.get(keyOf(identity, path)) ?? LOADING),
    ]),
  );
}
